import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter,
  HostListener, Input, Output, computed, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  allTimezones, browserTimezone, timezoneLabel,
} from '../../../features/pcheck/helpers/booking-timezone';

/**
 * Searchable timezone combobox over the full IANA list.
 * Value is an IANA id (e.g. "Asia/Kolkata"); defaults to the browser zone.
 * Emits `valueChange` when the user picks a zone.
 */
@Component({
  selector: 'app-timezone-select',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './timezone-select.component.html',
  styleUrls: ['./timezone-select.component.scss'],
})
export class TimezoneSelectComponent {
  /** Currently selected IANA id. Falls back to the browser zone when empty. */
  @Input() set value(v: string | null | undefined) {
    this._value.set(v && v.trim() ? v : browserTimezone());
  }
  get value(): string { return this._value(); }

  @Input() disabled = false;
  /** Optional compact styling for tight card headers. */
  @Input() compact = false;

  @Output() valueChange = new EventEmitter<string>();

  private readonly _value = signal(browserTimezone());
  readonly open  = signal(false);
  readonly query = signal('');

  private readonly _zones = allTimezones();

  readonly selectedLabel = computed(() => timezoneLabel(this._value()));

  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const list = q
      ? this._zones.filter(z =>
          z.toLowerCase().includes(q) || timezoneLabel(z).toLowerCase().includes(q))
      : this._zones;
    return list.slice(0, 200).map(z => ({ value: z, label: timezoneLabel(z) }));
  });

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  label(z: string): string { return timezoneLabel(z); }

  toggle(): void {
    if (this.disabled) return;
    this.open.update(v => !v);
    if (this.open()) this.query.set('');
  }

  onSearch(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  select(zone: string): void {
    this._value.set(zone);
    this.valueChange.emit(zone);
    this.open.set(false);
    this.query.set('');
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.open.set(false); }
}
